import React from "react";
import { Title, Text,  Stack, SimpleGrid, Paper, PaperProps, Group,createStyles, rem} from "@mantine/core";
import { PieChart, Pie, Cell, Tooltip /*BarChart, Bar, XAxis, YAxis, CartesianGrid*/, Legend } from "recharts";
import { IDonation, IPayout, ITip } from "../types";
import { IconReceipt2 } from "@tabler/icons-react";
import MonthlyDonationsChart from "../components/MonthlyDonationsChart";
import TransactionStatement from "../components/TransactionStatement";


const useStyles = createStyles((theme) => ({
    root: {
        padding: `calc(${theme.spacing.xl} * 1.5)`,
    },

    value: {
        fontSize: rem(24),
        fontWeight: 700,
        lineHeight: 1,
    },

    diff: {
        lineHeight: 1,
        display: 'flex',
        alignItems: 'center',
    },

    icon: {
        color: theme.colorScheme === 'dark' ? theme.colors.dark[3] : theme.colors.gray[5],
    },

    title: {
        fontWeight: 700,
        textTransform: 'uppercase',
    },
}));


interface StatsProps {
  donations: IDonation[];
  payouts: IPayout[];
  tips:ITip[];
}

const AdminStats: React.FC<StatsProps> = ({ donations, payouts,tips }) => {


  
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();

  const totalDonationsKES = donations.reduce((acc, d) => acc + (d.donation_currency === "KES" ? parseFloat(d.donation_amount) : 0), 0);
  const totalAvailable = donations.reduce((acc, d) => acc + parseFloat(d.available_amount.toString()), 0);
  const platformFeeIncome = donations.reduce((acc, d) => acc + parseFloat(d.platform_fee_in_ksh.toString()), 0);

  const monthlyDonations = donations.filter(d => {
    const date = new Date(d.createdAt);
    return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
  });
  
  const totalDonationsThisMonth = monthlyDonations.reduce((acc, d) => acc + parseFloat(d.donation_amount_in_ksh), 0);
  const totalAvailableThisMonth = monthlyDonations.reduce((acc, d) => acc + parseFloat(d.available_amount.toString()), 0);
  const totalIncomeThisMonth = monthlyDonations.reduce((acc, d) => acc + parseFloat(d.platform_fee_in_ksh.toString()), 0);
  
  const totalPayouts = payouts.reduce((acc, p) => acc + parseFloat(p.amount.toString()), 0);
  const totalPayoutsThisMonth = payouts.filter(p => {
    const date = new Date(p.date);
    return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
  }).reduce((acc, p) => acc + parseFloat(p.amount.toString()), 0);

  const pendingPayouts = totalAvailable - totalPayouts;

  let total =0;


  for(let i=0; i<tips.length;i++){
    const amount = parseFloat(tips[i].settled_amount_in_ksh.toString())
    total+=amount
  }

  //tips
  const totalTipAmount = total/*tips.reduce((acc, t) => acc + (t.donation_currency === "KES" ? parseFloat(t.amount.toString()) : 0), 0);*/


  const totalTipsThisMonth = tips.filter(p => {
    const date = new Date(p.date);
    return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
  }).reduce((acc, p) => acc + parseFloat(p.settled_amount_in_ksh.toString()), 0);
  const totalIncome = platformFeeIncome+totalTipAmount;

  const paymentMethodData = Object.entries(
    donations.reduce((acc: Record<string, number>, d) => {
      acc[d.payment_method] = (acc[d.payment_method] || 0) + parseFloat(d.donation_amount);
      return acc;
    }, {})
  ).map(([name, value]) => ({ name, value }));

  const payoutMethodData = Object.entries(
    payouts.reduce((acc: Record<string, number>, p) => {
      acc[p.paymentMethod?.payment_service] = (acc[p.paymentMethod?.payment_service] || 0) + parseFloat(p.amount.toString());
      return acc;
    }, {})
  ).map(([name, value]) => ({ name, value }));

  const {classes} = useStyles();
   const paperProps: PaperProps = {
          p: "md",
          shadow: "sm"
      }

  return (
    <div>

        <Stack spacing="xl">
            <Title order={3}>Donations & Income </Title>
              <SimpleGrid
              cols={4}
              breakpoints={[{maxWidth: 'md', cols: 2, spacing: 'md'}, {
              maxWidth: 'sm',
              cols: 1,
              spacing: 'sm'
             }]}>
                {/* Total donations to date */}
                <Paper {...paperProps}>
                     <Group position="apart">
                    <Text size="xs" color="dimmed" className={classes.title}>
                                                                Total Donations
                    </Text>
                    <IconReceipt2 className={classes.icon} size="1.4rem" stroke={1.5}/>
                    </Group>
                        
                    <Group align="flex-end" spacing="xs" mt={25}>
                    <Text className={classes.value}>KES {totalDonationsKES.toFixed(2)}</Text>
                                                         
                        </Group>
                        
                <Text fz="xs" c="dimmed" mt={7}>
                    Donations todate
                    </Text>

                </Paper>
                {/* Net for for fundraisers */}

                <Paper {...paperProps}>
                     <Group position="apart">
                    <Text size="xs" color="dimmed" className={classes.title}>
                    Fundraisers Net
                    </Text>
                    <IconReceipt2 className={classes.icon} size="1.4rem" stroke={1.5}/>
                    </Group>
                        
                    <Group align="flex-end" spacing="xs" mt={25}>
                    <Text className={classes.value}>KES {totalAvailable.toFixed(2)}</Text>
                                                         
                        </Group>
                        
                <Text fz="xs" c="dimmed" mt={7}>
                    Fundraisers Earnings to date
                    </Text>

                </Paper>
                {/* company income */}

                <Paper {...paperProps}>
                     <Group position="apart">
                    <Text size="xs" color="dimmed" className={classes.title}>
                    Income
                    </Text>
                    <IconReceipt2 className={classes.icon} size="1.4rem" stroke={1.5}/>
                    </Group>
                        
                    <Group align="flex-end" spacing="xs" mt={25}>
                    <Text className={classes.value}>KES {totalIncome.toFixed(2)}</Text>
                                                         
                        </Group>
                        
                <Text fz="xs" c="dimmed" mt={7}>
                    Platform Fee Income <strong>{platformFeeIncome.toFixed(2)}</strong>
                    </Text>
                    <Text fz="xs" c="dimmed" mt={7}>
                    Tips<strong>{(totalIncome-platformFeeIncome).toFixed(2)}</strong>
                    </Text>

                </Paper>
                {/* Platform income*/}

               

                <Paper {...paperProps}>
                     <Group position="apart">
                    <Text size="xs" color="dimmed" className={classes.title}>
                    Tips
                    </Text>
                    <IconReceipt2 className={classes.icon} size="1.4rem" stroke={1.5}/>
                    </Group>
                        
                    <Group align="flex-end" spacing="xs" mt={25}>
                    <Text className={classes.value}>KES {(totalIncome-platformFeeIncome).toFixed(2)}</Text>
                                                         
                        </Group>
                        
                <Text fz="xs" c="dimmed" mt={7}>
                    % of Income <strong>{(((totalIncome-platformFeeIncome)/totalIncome)*100).toFixed(2)}%</strong>
                    </Text>
                 

                </Paper>

       </SimpleGrid>

       {/* Charts*/}

       <SimpleGrid
              cols={3}
              breakpoints={[{maxWidth: 'md', cols: 2, spacing: 'md'}, {
              maxWidth: 'sm',
              cols: 1,
              spacing: 'sm'
             }]}>
                {/* Total donations to date */}
                <Paper {...paperProps}>
                     <Group position="apart">
                     <PieChart width={400} height={300}>
        <Pie data={paymentMethodData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80}>
          {paymentMethodData.map((index) => (
            <Cell key={`cell-${index}`} fill={"#" + Math.floor(Math.random() * 16777215).toString(16)} />
          ))}
        </Pie>
        <Tooltip />
        <Legend />
      </PieChart>
                    </Group>
                        
                    <Group align="flex-end" spacing="xs" mt={25}>
                    <Text className={classes.value}>KES {totalDonationsKES.toFixed(2)}</Text>
                                                         
                        </Group>
                        
                <Text fz="xs" c="dimmed" mt={7}>
                    Donations by payment methods
                    </Text>

                </Paper>
                {/* Net for for fundraisers */}

                <Paper {...paperProps}>
                     <Group position="apart">
                     <PieChart width={400} height={300}>
        <Pie data={payoutMethodData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80}>
          {payoutMethodData.map((index) => (
            <Cell key={`cell-${index}`} fill={"#" + Math.floor(Math.random() * 16777215).toString(16)} />
          ))}
        </Pie>
        <Tooltip />
        <Legend />
      </PieChart>
                    <IconReceipt2 className={classes.icon} size="1.4rem" stroke={1.5}/>
                    </Group>
                        
                    <Group align="flex-end" spacing="xs" mt={25}>
                    <Text className={classes.value}>KES {totalAvailable.toFixed(2)}</Text>
                                                         
                        </Group>
                        
                <Text fz="xs" c="dimmed" mt={7}>
                    Payouts by payment methods
                    </Text>

                </Paper>
               
                {/* Platform income*/}

               

                <Paper {...paperProps}>
                     <Group position="apart">
                    <Text size="xs" color="dimmed" className={classes.title}>
                    Monthly contributions bar chart
                    </Text>
                    <IconReceipt2 className={classes.icon} size="1.4rem" stroke={1.5}/>
                    </Group>
                        
                    <Group align="flex-end" spacing="xs" mt={25}>
                    <MonthlyDonationsChart donations={donations}/>
                                                         
                        </Group>
                        
               
                

                </Paper>

       </SimpleGrid>

       <TransactionStatement/>


       <SimpleGrid
              cols={3}
              breakpoints={[{maxWidth: 'md', cols: 2, spacing: 'md'}, {
              maxWidth: 'sm',
              cols: 1,
              spacing: 'sm'
             }]}>
                {/* Total donations to date */}        
                

                <Paper {...paperProps}>
                     <Group position="apart">
                    <Text size="xs" color="dimmed" className={classes.title}>
                    Current Donations
                    </Text>
                    <IconReceipt2 className={classes.icon} size="1.4rem" stroke={1.5}/>
                    </Group>
                        
                    <Group align="flex-end" spacing="xs" mt={25}>
                    <Text className={classes.value}>KES {totalDonationsThisMonth.toFixed(2)}</Text>
                                                         
                        </Group>
                        
                <Text fz="xs" c="dimmed" mt={7}>
                    Current Month Donations
                    </Text>

                </Paper>

                <Paper {...paperProps}>
                     <Group position="apart">
                    <Text size="xs" color="dimmed" className={classes.title}>
                    Current Fundraiser Amount
                    </Text>
                    <IconReceipt2 className={classes.icon} size="1.4rem" stroke={1.5}/>
                    </Group>
                        
                    <Group align="flex-end" spacing="xs" mt={25}>
                    <Text className={classes.value}>KES {totalAvailableThisMonth.toFixed(2)}</Text>
                                                         
                        </Group>
                        
                <Text fz="xs" c="dimmed" mt={7}>
                    Current Month Unsettled Fundraiser Money <strong>000</strong>
                    </Text>

                 

                </Paper>


                <Paper {...paperProps}>
                     <Group position="apart">
                    <Text size="xs" color="dimmed" className={classes.title}>
                    Current Month Income
                    </Text>
                    <IconReceipt2 className={classes.icon} size="1.4rem" stroke={1.5}/>
                    </Group>
                        
                    <Group align="flex-end" spacing="xs" mt={25}>
                    <Text className={classes.value}>KES {totalIncomeThisMonth.toFixed(2)}</Text>
                                                         
                        </Group>
                        
                <Text fz="xs" c="dimmed" mt={7}>
                    Platform Fee <strong>{(totalIncomeThisMonth-totalTipsThisMonth).toFixed(2)}</strong>
                    </Text>

                    <Text fz="xs" c="dimmed" mt={7}>
                    Tips <strong>{totalTipsThisMonth.toFixed(2)}</strong>
                    </Text>

                </Paper>

               



             

       </SimpleGrid>

     


       <Title order={3}>Payouts</Title>
              <SimpleGrid
              cols={4}
              breakpoints={[{maxWidth: 'md', cols: 2, spacing: 'md'}, {
              maxWidth: 'sm',
              cols: 1,
              spacing: 'sm'
             }]}>
                {/* Total donations to date */}
                <Paper {...paperProps}>
                     <Group position="apart">
                    <Text size="xs" color="dimmed" className={classes.title}>
                                                                Total Payments
                    </Text>
                    <IconReceipt2 className={classes.icon} size="1.4rem" stroke={1.5}/>
                    </Group>
                        
                    <Group align="flex-end" spacing="xs" mt={25}>
                    <Text className={classes.value}>KES {totalPayouts.toFixed(2)}</Text>
                                                         
                        </Group>
                        
                <Text fz="xs" c="dimmed" mt={7}>
                    Payouts to date
                    </Text>

                </Paper>
                {/* Net for for fundraisers */}

                <Paper {...paperProps}>
                     <Group position="apart">
                    <Text size="xs" color="dimmed" className={classes.title}>
                    Outstanding Payments
                    </Text>
                    <IconReceipt2 className={classes.icon} size="1.4rem" stroke={1.5}/>
                    </Group>
                        
                    <Group align="flex-end" spacing="xs" mt={25}>
                    <Text className={classes.value}>KES {pendingPayouts.toFixed(2)}</Text>
                                                         
                        </Group>
                        
                <Text fz="xs" c="dimmed" mt={7}>
                    Unsetled Fundraiser Money
                    </Text>

                </Paper>
                {/* company income */}

                <Paper {...paperProps}>
                     <Group position="apart">
                    <Text size="xs" color="dimmed" className={classes.title}>
                    Current Payments
                    </Text>
                    <IconReceipt2 className={classes.icon} size="1.4rem" stroke={1.5}/>
                    </Group>
                        
                    <Group align="flex-end" spacing="xs" mt={25}>
                    <Text className={classes.value}>KES {totalPayoutsThisMonth.toFixed(2)}</Text>
                                                         
                        </Group>
                        
                <Text fz="xs" c="dimmed" mt={7}>
                    Payments made this month
                    </Text>

                </Paper>
                {/* Platform income*/}

               

                <Paper {...paperProps}>
                     <Group position="apart">
                    <Text size="xs" color="dimmed" className={classes.title}>
                    Total Gross Income
                    </Text>
                    <IconReceipt2 className={classes.icon} size="1.4rem" stroke={1.5}/>
                    </Group>
                        
                    <Group align="flex-end" spacing="xs" mt={25}>
                    <Text className={classes.value}>KES {totalIncome.toFixed(2)}</Text>
                                                         
                        </Group>
                        
                <Text fz="xs" c="dimmed" mt={7}>
                    Platform Fee <strong>{(totalIncome-totalTipsThisMonth).toFixed(2)}</strong>
                    </Text>
                    <Text fz="xs" c="dimmed" mt={7}>
                    Tips <strong>{totalTipsThisMonth.toFixed(2)}</strong>
                    </Text>

                </Paper>

       </SimpleGrid>

        </Stack>
 

  

  

    
    </div>
  );
};

export default AdminStats;